import React, { useMemo, useCallback, useEffect, useRef } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Box,
  CircularProgress,
  InputAdornment,
} from "@material-ui/core";
import { MuiAccountDropDown } from "../../../../components/common/MuiAccountDropDown";
import { MuiProductDropDown } from "../../../../components/common/MuiProductDropDown";
import {
  readTrackSelection,
  writeTrackSelection,
} from "../../../../managerApp/resources/trackCars/TrackContext";
import { getTrackDisplayName } from "../utils/trackLocationUtils";

export const TrackControl = ({
  isTrackDisabled,
  tracks,
  accountFilter,
  setAccountFilter,
  onAccountsLoaded,
  productFilter,
  setProductFilter,
  leftTrack,
  leftTrackLoading = false,
  handleLeftTrackChange,
  classes,
  isTabletOrLandscape,
  rightTrack,
  setRightTrack,
  setRightTrackId,
  rightTrackLoading = false,
}) => {
  const trackOptions = useMemo(() => tracks || [], [tracks]);
  const hydratedRef = useRef(false);
  const leftTrackValue = useMemo(
    () =>
      trackOptions.some((t) => t.id === leftTrack?.id) ? leftTrack.id : "",
    [trackOptions, leftTrack],
  );
  const rightTrackValue = useMemo(
    () =>
      trackOptions.some((t) => t.id === rightTrack?.id) ? rightTrack.id : "",
    [trackOptions, rightTrack],
  );

  const handleRightTrackChange = useCallback(
    (trackId) => {
      const track = tracks.find((t) => t.id === trackId);
      setRightTrack(track || null);
      if (track) {
        const trackLocationId = track?.locations?.items?.[0]?.id;
        setRightTrackId(trackLocationId || null);
      } else {
        setRightTrackId(null);
      }
    },
    [tracks, setRightTrack, setRightTrackId],
  );

  useEffect(() => {
    if (hydratedRef.current) return;
    if (!trackOptions.length) return;
    const saved = readTrackSelection();
    if (!saved) return;

    if (saved.leftTrackId && saved.leftTrackId !== leftTrack?.id) {
      handleLeftTrackChange(saved.leftTrackId);
    }
    if (
      isTabletOrLandscape &&
      saved.rightTrackId &&
      saved.rightTrackId !== rightTrack?.id
    ) {
      handleRightTrackChange(saved.rightTrackId);
    }
    hydratedRef.current = true;
  }, [
    trackOptions,
    leftTrack,
    rightTrack,
    isTabletOrLandscape,
    handleLeftTrackChange,
    handleRightTrackChange,
  ]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!leftTrack?.id && !rightTrack?.id) return;
    writeTrackSelection({
      leftTrackId: leftTrack?.id || null,
      rightTrackId: isTabletOrLandscape ? rightTrack?.id || null : null,
    });
  }, [leftTrack, rightTrack, isTabletOrLandscape]);

  const handleAccountChange = (selectedAccount) => {
    setAccountFilter(selectedAccount || null);
    if (productFilter) {
      setProductFilter(null);
    }
  };

  const handleProductChange = (selectedProduct) => {
    setProductFilter(selectedProduct || null);
  };

  return (
    <Box width="100%" px={2} mb={1}>
      <Grid container spacing={2} justify="center" alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <MuiAccountDropDown
            label="Account"
            value={accountFilter || null}
            onChange={handleAccountChange}
            onAccountsLoaded={onAccountsLoaded}
            fullWidth
            clearOnEscape
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MuiProductDropDown
            label="Product"
            accountId={accountFilter?.id}
            value={productFilter || null}
            onChange={handleProductChange}
            disabled={!accountFilter?.id}
            fullWidth
            clearOnEscape
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            id="track"
            label={isTabletOrLandscape ? "Left Track" : "Active Track"}
            variant="outlined"
            select
            margin="none"
            size="small"
            disabled={isTrackDisabled()}
            required
            fullWidth
            value={leftTrackValue}
            onChange={(evt) => handleLeftTrackChange(evt.target.value)}
            InputProps={{
              endAdornment: leftTrackLoading ? (
                <InputAdornment position="end" style={{ marginRight: 24 }}>
                  <CircularProgress color="inherit" size={20} />
                </InputAdornment>
              ) : null,
            }}
          >
            {trackOptions.map((track) => (
              <MenuItem key={track.id} value={track.id}>
                {getTrackDisplayName(track)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {isTabletOrLandscape && (
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="targetTrack"
              label="Right Track"
              variant="outlined"
              select
              margin="none"
              size="small"
              disabled={isTrackDisabled()}
              fullWidth
              value={rightTrackValue}
              onChange={(evt) => handleRightTrackChange(evt.target.value)}
              InputProps={{
                endAdornment: rightTrackLoading ? (
                  <InputAdornment position="end" style={{ marginRight: 24 }}>
                    <CircularProgress color="inherit" size={20} />
                  </InputAdornment>
                ) : null,
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {trackOptions.map((track) => (
                <MenuItem key={track.id} value={track.id}>
                  {getTrackDisplayName(track)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
